import { OrderService } from 'src/app/services/order.service';
import { ClientDetailsService } from 'src/app/services/client-details.service';
import { AnalyticsService } from 'src/app/services/analytics.service';
import {
  ChangeDetectorRef,
  Component,
  OnInit,
  Output,
  EventEmitter,
  Input,
  HostListener,
  inject
} from '@angular/core';
import { DeviceDetectorService } from 'ngx-device-detector';

import { WebcamImage, WebcamInitError, WebcamUtil } from 'ngx-webcam';
import { Observable, Subject } from 'rxjs';
@Component({
    selector: 'app-kyc-details',
    templateUrl: './kyc-details.component.html',
    styleUrls: ['./kyc-details.component.scss'],
    standalone: false
})
export class KycDetailsComponent implements OnInit {
  private analytics = inject(AnalyticsService);
  @Input() showWebcam: boolean;
  @Input() orderId: string | number;
  showSign: boolean = true;
  @HostListener('window:resize', ['$event'])
  onResize(event: Event): void {
    this.showSign = window.innerWidth > 740;
  }
  ipAddress: any;
  latitude: number;
  longitude: number;
  locationError: string;
  @Output()
  public pictureTaken = new EventEmitter<WebcamImage>();

  // toggle webcam on/off
  public allowCameraSwitch = true;
  public multipleWebcamsAvailable = false;
  public deviceId: string;
  imageBase64: any;
  public videoOptions: MediaTrackConstraints = {
    facingMode: 'user',
  };

  public videoMediaStreamConstraints: MediaStreamConstraints = {
    video: this.videoOptions,
  };
  public errors: WebcamInitError[] = [];
  private trigger: Subject<void> = new Subject<void>();
  private nextWebcam: Subject<boolean | string> = new Subject<
    boolean | string
  >();
  formattedTime: any;
  deviceInfo: import('ngx-device-detector').DeviceInfo;
  cameraReady: boolean = false;
  private readonly MIN_IMAGE_SIZE_KB = 10; // Minimum image size in KB
  private readonly MIN_IMAGE_SIZE_BYTES = this.MIN_IMAGE_SIZE_KB * 1024;

  constructor(
    private ClientDetailsService: ClientDetailsService,
    private deviceService: DeviceDetectorService,
    private orderService: OrderService,
    private cdr: ChangeDetectorRef
  ) {
    this.showSign = window.innerWidth > 740;
  }

  ngOnInit() {
    this.requestCameraPermission();
    WebcamUtil.getAvailableVideoInputs().then(
      (mediaDevices: MediaDeviceInfo[]) => {
        this.multipleWebcamsAvailable = mediaDevices && mediaDevices.length > 1;
      }
    );
  }

  requestCameraPermission() {
  this.cameraReady = false;
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this.analytics.logCameraDebug('error', 'Browser lacks API support', 'MediaDevicesNotSupported');
      this.cameraReady = true; 
      return;
    }

    navigator.mediaDevices
      .getUserMedia(this.videoMediaStreamConstraints)
      .then((stream) => {
      // Get the video track
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
          const settings = videoTrack.getSettings();
          const resolution = settings.width ? `${settings.width}x${settings.height}` : 'Unknown Resolution';
          this.analytics.logCameraDebug('success', videoTrack.label, undefined, resolution);
        }

      // Wait for the track to be 'live' and have dimensions
      const checkVideoReady = setInterval(() => {
        const settings = videoTrack.getSettings();
        
        // Check if video track has dimensions (actual video feed)
        if (settings.width && settings.height && videoTrack.readyState === 'live') {
          clearInterval(checkVideoReady);
          // Add small buffer after dimensions are available
          setTimeout(() => {
            this.cameraReady = true;
            this.cdr.detectChanges();
          }, 500);
        }
      }, 100);
      
      // Fallback timeout if check doesn't complete
      setTimeout(() => {
        clearInterval(checkVideoReady);
        if (!this.cameraReady) {
          this.cameraReady = true;
          this.cdr.detectChanges();
        }
      }, 5000);
    })
    .catch((error) => {
      this.cameraReady = false;
      this.analytics.logCameraDebug(
          'error', 
          error.message || 'Camera blocked or unavailable', 
          error.name || 'NotAllowedError'
        );
      this.errors.push({
        message: 'Camera access denied or unavailable',
        mediaStreamError: error
      } as WebcamInitError);
    });
}

  triggerSnapshot(): void {
    this.trigger.next();
  }

  toggleWebcam(): void {
    this.showWebcam = !this.showWebcam;
  }

  handleInitError(error: WebcamInitError): void {
    this.errors.push(error);
    this.analytics.logSystemError('camera_init', error.message || 'Permission Denied', this.orderId);
    this.pictureTaken.emit();
  }

  handleImage(webcamImage: WebcamImage): void {
  // console.log('--- KycDetailsComponent: handleImage triggered ---', webcamImage);
  if (!this.cameraReady) {
    this.pictureTaken.emit(null);
    return;
  }

  // Validate image exists and has data
  if (!webcamImage || !webcamImage.imageAsDataUrl) {
    this.pictureTaken.emit(null);
    return;
  }
  
  // Validate base64 length (black images are typically under 10KB)
  const base64Data = webcamImage.imageAsDataUrl.split(',')[1];
  const estimatedSizeBytes = (base64Data.length * 3) / 4;
  
  if (estimatedSizeBytes < this.MIN_IMAGE_SIZE_BYTES) {
      // console.warn(`Image size (${(estimatedSizeBytes / 1024).toFixed(2)} KB) is below minimum required (${this.MIN_IMAGE_SIZE_KB} KB)`);
      const sizeKb = (estimatedSizeBytes / 1024).toFixed(2);
      this.analytics.logSystemError('IMAGE_CAPTURE_QUALITY', `Image size too small: ${sizeKb} KB`, this.orderId);
      this.pictureTaken.emit(null);
      return;
    }
    this.imageBase64 = webcamImage.imageAsDataUrl;
    this.pictureTaken.emit(this.imageBase64);
  }

  retakeImage() {
    this.showWebcam = true;
    this.imageBase64 = null;
  }
  cancelImage() {
    this.showWebcam = true;
    this.imageBase64 = null;
    this.pictureTaken.emit();
  }
  cameraWasSwitched(deviceId: string): void {
    this.deviceId = deviceId;
  }

  get triggerObservable(): Observable<void> {
    return this.trigger.asObservable();
  }

  get nextWebcamObservable(): Observable<boolean | string> {
    return this.nextWebcam.asObservable();
  }
}